import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { trigger, state, style, transition, animate } from '@angular/animations';

interface ContactForm {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

@Component({
  selector: 'app-contact',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.css'],
  animations: [
    trigger('fadeInUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(40px)' }),
        animate('600ms cubic-bezier(0.25, 0.8, 0.25, 1)',
          style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('slideInLeft', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-50px)' }),
        animate('800ms 200ms cubic-bezier(0.25, 0.8, 0.25, 1)',
          style({ opacity: 1, transform: 'translateX(0)' }))
      ])
    ]),
    trigger('slideInRight', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(50px)' }),
        animate('800ms 400ms cubic-bezier(0.25, 0.8, 0.25, 1)',
          style({ opacity: 1, transform: 'translateX(0)' }))
      ])
    ])
  ]
})
export class ContactComponent implements OnInit {
  contactForm: ContactForm = {
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  };

  isSubmitting = false;
  isSubmitted = false;

  contactInfo = [
    {
      icon: '📍',
      title: 'Our Address',
      details: [
        'Aadinath Enterprises',
        'Near Amrut Paradise, Next to Shri Ganesh Parking',
        'Manisha Nagar Gate No-2, Kalwa',
        'Thane - 400605, Maharashtra, India'
      ]
    },
    {
      icon: '📞',
      title: 'Phone Numbers',
      details: [
        '+91 9768301159',
        '+91 9769053769',
        '+91 7506381989',
        'Available 9 AM - 9 PM'
      ]
    },
    {
      icon: '✉️',
      title: 'Email Address',
      details: [
        'aadinathenterprises21@gmail.com',
        'sales@aadinathentp.com',
        'info@aadinathentp.com',
        'Response within 24 hours'
      ]
    }
  ];

  socialLinks = [
    { name: 'Facebook', icon: 'fab fa-facebook-f', url: 'https://facebook.com/aadinathenterprises' },
    { name: 'Instagram', icon: 'fab fa-instagram', url: 'https://instagram.com/aadinathenterprises' },
    { name: 'LinkedIn', icon: 'fab fa-linkedin-in', url: 'https://linkedin.com/company/aadinathenterprises' },
    { name: 'WhatsApp', icon: 'fab fa-whatsapp', url: 'https://wa.me/919768301159' }
  ];

  faqs = [
    {
      question: 'What is your minimum order quantity?',
      answer: 'MOQ varies by product. No minimum for retail orders, contact us for wholesale pricing.'
    },
    {
      question: 'Do you offer customization?',
      answer: 'Yes, we provide logo printing, color variations, and custom designs. Contact us for details.'
    },
    {
      question: 'What is your delivery time?',
      answer: 'Standard delivery: 5-7 days for metros, 7-10 days for other locations. Express available.'
    },
    {
      question: 'Do you ship internationally?',
      answer: 'Currently India only. International shipping coming soon. Contact us for inquiries.'
    }
  ];

  constructor() { }

  ngOnInit(): void {
    // Component initialization
  }

  onSubmit(): void {
    if (this.isValidForm()) {
      this.isSubmitting = true;
      // Simulate form submission - replace with actual API call
      setTimeout(() => {
        this.isSubmitting = false;
        this.isSubmitted = true;
        this.resetForm();
        // Reset success message after 5 seconds
        setTimeout(() => {
          this.isSubmitted = false;
        }, 5000);
      }, 2000);
    }
  }

  isValidForm(): boolean {
    return !!(
      this.contactForm.name.trim() &&
      this.contactForm.email.trim() &&
      this.validateEmail(this.contactForm.email) &&
      this.contactForm.phone.trim() &&
      this.contactForm.subject.trim() &&
      this.contactForm.message.trim()
    );
  }

  private validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  resetForm(): void {
    this.contactForm = {
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    };
  }

  openSocialLink(url: string): void {
    if (url !== '#') {
      window.open(url, '_blank');
    }
  }

  // WhatsApp Quote Functionality
  requestWhatsAppQuote(): void {
    const message = this.generateQuoteMessage();
    const whatsappUrl = `https://wa.me/919768301159?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  }

  private generateQuoteMessage(): string {
    const formData = {
      name: this.contactForm.name || '[Please provide your name]',
      email: this.contactForm.email || '[Please provide your email]',
      phone: this.contactForm.phone || '[Please provide your phone]',
      subject: this.contactForm.subject || 'General Inquiry',
      message: this.contactForm.message || 'I would like to get more information about your products and services.'
    };

    return `Hello Aadinath Enterprises! 👋

I'm interested in connecting with you. Here are my details:

📝 *Contact Details:*
• Name: ${formData.name}
• Email: ${formData.email}
• Phone: ${formData.phone}
• Subject: ${formData.subject}

💬 *Message:*
${formData.message}

I look forward to hearing from you soon!

Thank you! 🙏`;
  }
}
